import React, { useContext, useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import injectStyles from 'react-jss'
import { Box, Grid } from '@material-ui/core'
import { userLogin } from 'Reducers/Auth/actions'
import { authorization } from 'Module/Auth/Auth'
import { PATHS } from 'Config/Routing/paths'
import { getNextLink } from '../../../Utils/Link'
import Input from '../../../Components/Inputs/Input'
import InputPassword from '../../../Components/Inputs/InputPassword'
import SubmitButton from '../../../Components/Form/SubmitButton'
import Link from '../../../Components/Link'

import AuthWrapper from '../AuthWrapper'
import { styles } from './styles'
import { useWrappedPosthog } from '../../../Components/PostHogProvider/hooks'
import { SocialAuthButton } from '../components/SocialAuthButton/SocialAuthButton'
import { OrSeparate } from '../components/OrSeparate/OrSeparate'
import { AUTH_METHOD } from '../../../constants'
import { SnackBarContext } from '../../../Components/SnackBar'
import { CustomersTestimonials } from '../components/CustomersTestimonials/CustomerTestimonials'
import useQuery from '../../../Utils/Hooks/useQuery'

const Login = ({ classes, history }) => {
  const posthog = useWrappedPosthog()
  const query = useQuery()
  const [fields, setFields] = useState({
    email: { value: '', error: true, isTouched: false },
    password: { value: '', error: true, isTouched: false },
  })

  const user = useSelector(state => state.auth.user)
  const errorMessage = useSelector(state => state.auth.errorMessage)

  const getShopifyParams = () => {
    try {
      const shopifyConnectionStatus = query.get('status')
      const credentialId = query.get('credentialId')
      const shopName = query.get('shopName')
      const shopDomain = query.get('shopDomain')
      return { shopifyConnectionStatus, credentialId, shopName, shopDomain }
    } catch (error) {
      return {
        shopifyConnectionStatus: null,
        credentialId: null,
        shopName: null,
        shopDomain: null,
      }
    }
  }

  const {
    shopifyConnectionStatus,
    credentialId,
    shopName,
    shopDomain,
  } = useMemo(() => getShopifyParams(), [query])

  const dispatch = useDispatch()
  const runSnackBar = useContext(SnackBarContext)

  const openSnackBar = (type, message) => {
    runSnackBar({
      type: type,
      vertical: 'top',
      msg: message,
      autoHideDuration: 6000,
      key: new Date().getTime(),
    })
  }

  useEffect(() => {
    if (authorization.isLogged()) {
      if (shopifyConnectionStatus && credentialId) {
        window.location.href = getNextLink(
          `/dashboard/callbacks/shopify?status=${shopifyConnectionStatus}&credentialId=${credentialId}&shopName=${shopName}&shopDomain=${shopDomain}`,
        )
      } else {
        history.push(PATHS.main.dashboard)
      }
    }
  }, [user, shopifyConnectionStatus, credentialId])

  useEffect(() => {
    const interval = setInterval(() => {
      if (authorization.isLogged()) {
        if (shopifyConnectionStatus && credentialId) {
          window.location.href = getNextLink(
            `/dashboard/callbacks/shopify?status=${shopifyConnectionStatus}&credentialId=${credentialId}&shopName=${shopName}&shopDomain=${shopDomain}`,
          )
        } else {
          history.push(PATHS.main.dashboard)
        }
        clearInterval(interval)
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [shopifyConnectionStatus, credentialId, shopName, shopDomain])

  useEffect(() => {
    const handleStorageChange = e => {
      if (e.key === 'signUpData') {
        const signUpData = JSON.parse(e.newValue)
        if (signUpData) {
          history.push({
            pathname: PATHS.main.signUp,
            state: { signUpData },
          })
        }
      }
      if (e.key === 'social_login_error') {
        openSnackBar('error', e.newValue)
        localStorage.removeItem('social_login_error')
      }
    }

    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [history])

  const onChange = type => (value, error) => {
    setFields(prev => ({
      ...prev,
      [type]: {
        value,
        error: !value || !!error,
        isTouched: true,
      },
    }))
  }

  const onSubmit = () => {
    if (posthog) {
      posthog.capture('Logged in', {
        'event_category': 'Login',
      })
    }
    const isFieldsHasError = Object.values(fields).some(({ error }) => error)
    if (isFieldsHasError) {
      setFields(prev => {
        return {
          email: {
            value: prev.email.value,
            error: prev.email.error,
            isTouched: true,
          },
          password: {
            value: prev.password.value,
            error: prev.password.error,
            isTouched: true,
          },
        }
      })
    } else {
      dispatch(userLogin(fields.email.value, fields.password.value))
    }
  }
  const handleKeyPress = e => {
    if (e.key === 'Enter') {
      e.preventDefault()
      onSubmit(e)
    }
  }

  return (
    <AuthWrapper>
      <Grid container spacing={4}>
        <Grid
          container
          item
          xs={12}
          md={6}
          alignItems="center"
          justifyContent="center"
        >
          <Grid
            container
            item
            xs={12}
            alignItems="center"
            justifyContent="center"
            className={'content has-large-padding h-100'}
          >
            <Box className="content-inner" onKeyDown={handleKeyPress}>
              <h1 className={'text-left'} style={{ margin: '0px 0px 32px' }}>
                {'Login to your account'}
              </h1>
              {process.env.REACT_APP_NODE_ENV !== 'promofy' && (
                <React.Fragment>
                  <div className={classes.buttonsContainer}>
                    <SocialAuthButton type="google" />
                  </div>
                  <OrSeparate />
                </React.Fragment>
              )}
              <div cypress_id={'emailAddress'}>
                <Input
                  label="Email address"
                  value={fields.email.value}
                  onChange={onChange('email')}
                  required
                  email
                  isTouched={fields.email.isTouched}
                  inputProps={{ type: 'email' }}
                />
              </div>
              <div cypress_id={'password'}>
                <InputPassword
                  authMethod={AUTH_METHOD.OPPIZI}
                  value={fields.password.value}
                  onChange={onChange('password')}
                  disableValidation={true}
                  isTouched={fields.password.isTouched}
                />
              </div>
              <div
                className={classes.forgotPassword}
                cypress_id={'forgotPassw'}
                onClick={() => {
                  if (posthog) {
                    posthog.capture('Forgot Password', {
                      'event_category': 'Login',
                    })
                  }
                }}
              >
                <Link
                  text="Forgot your password?"
                  to={PATHS.main.requestPass}
                />
              </div>
              <div cypress_id={'loginBtn'}>
                <SubmitButton
                  className={'signUpButton'}
                  onClick={onSubmit}
                  disabled={false}
                >
                  Log in
                </SubmitButton>
                {errorMessage ? (
                  <div className={'error'}>{errorMessage}</div>
                ) : null}
              </div>
              <div className={'link text-center'}>
                Don’t have an account yet?{' '}
                <Link
                  text="Sign up"
                  to={`${PATHS.main.signUp}${
                    shopifyConnectionStatus && credentialId
                      ? `?status=${shopifyConnectionStatus}&credentialId=${credentialId}&shopName=${shopName}&shopDomain=${shopDomain}`
                      : ''
                  }`}
                />
                .
              </div>
              <div className={'link'}>
                Want to work with us? Sign in as a{' '}
                <a
                  href="https://www.oppizi.com/brand-ambassadors"
                  className={'links'}
                >
                  Brand Ambassador
                </a>
                .
              </div>
            </Box>
          </Grid>
        </Grid>
        <Grid
          container
          item
          xs={12}
          md={6}
          justifyContent="center"
          alignItems="center"
          direction="column"
        >
          <CustomersTestimonials />
        </Grid>
      </Grid>
    </AuthWrapper>
  )
}

export default injectStyles(styles)(Login)
